<script setup lang="ts">
import axios from 'axios'

import FormColorPicker from '@/components/ui/ColorPicker/ColorPicker.vue'
import { Loader2 } from 'lucide-vue-next'

import useMedia from '@/composables/media'
import useUrl from '@/composables/url'

import type { ImageFile } from '@/types/types'

interface ColorsData {
  background: string
  button: string
  text: string
}
const { t } = useI18n()
const { hasPermissionBySlug } = useAccess()
const { formattedRedirectUrl } = useUrl()
const { merchantName, merchantLogo, user } = useLocalStorage()
const { isDefaultLogo } = useMedia()
const { getPredominantColors, getContrast } = useColor()

const merchantEmailLogo = ref<string>('')
const merchantSquareLogo = ref<string>('')

const logoImages = ref<ImageFile[]>([])
const logoInitialImages = ref<ImageFile[]>([])
const logoDeletedImages = ref<number[]>([])
const squareLogoImages = ref<ImageFile[]>([])
const squareLogoInitialImages = ref<ImageFile[]>([])
const squareLogoDeletedImages = ref<number[]>([])
const emailLogoImages = ref<ImageFile[]>([])
const emailLogoInitialImages = ref<ImageFile[]>([])
const emailLogoDeletedImages = ref<number[]>([])
const notification = useNotification()
const route = useRoute()
const router = useRouter()
const defaultColor = '#999dc4'
const defaultFormValues = {
  logo: null,
  squareLogo: null,
  emailLogo: null,
  background: null,
  button: null,
  text: null
}

const saveLoading = ref(false)
const fetchLoading = ref(true)

const merchantColors = ref<ColorsData>({
  background: null,
  button: null,
  text: null
})

const form = ref<{
  logo: string
  squareLogo: string
  emailLogo: string
  background: string
  button: string
}>({
  ...defaultFormValues
})

const fetchColors = async () => {
  const { data } = await axios.get<{
    data: ColorsData
  }>(`merchants/${user.value.merchant.id}/colors`)
  const resColors = data.data

  merchantColors.value = JSON.parse(JSON.stringify(resColors))
  form.value = {
    ...defaultFormValues,
    ...resColors
  }
}

const fetchMedia = async () => {
  const { data } = await axios.get(`merchants/${user.value.merchant.id}/media`)
  const { logo, logoSquare, logoMail } = data.data

  merchantEmailLogo.value = logoMail
  merchantSquareLogo.value = logoSquare
  merchantLogo.value = logo
}

const syncInitialImages = () => {
  logoInitialImages.value = displayedLogo.value ? [{ id: 1, path: displayedLogo.value, name: 'merchant-logo' }] : []
  emailLogoInitialImages.value = merchantEmailLogo.value
    ? [{ id: 2, path: merchantEmailLogo.value, name: 'merchant-email-logo' }]
    : []
  squareLogoInitialImages.value = merchantSquareLogo.value
    ? [{ id: 3, path: merchantSquareLogo.value, name: 'merchant-square-logo' }]
    : []
}

onMounted(async () => {
  await Promise.all([fetchColors(), fetchMedia()])
  fetchLoading.value = false
  syncInitialImages()
})

const displayedLogo = computed(() => {
  return isDefaultLogo(merchantLogo.value) ? null : merchantLogo.value
})

// Un logo déjà carré fait un favicon correct tel quel : inutile d'en demander un second.
const squareRatioTolerance = 0.1
const isLogoSquare = ref(false)
const currentLogoSource = computed(() => {
  if (form.value.logo) return form.value.logo
  // Corbeille cliquée sur le logo initial : on le traite comme absent tant qu'il n'est pas remplacé.
  if (logoDeletedImages.value.length > 0) return null
  return displayedLogo.value
})

watch(
  currentLogoSource,
  (source) => {
    if (!source) {
      isLogoSquare.value = false
      return
    }
    const image = new Image()
    image.onload = () => {
      isLogoSquare.value = Math.abs(image.naturalWidth / image.naturalHeight - 1) <= squareRatioTolerance
    }
    image.onerror = () => {
      isLogoSquare.value = false
    }
    image.src = source
  },
  { immediate: true }
)

const hasSquareLogo = computed(() => {
  if (squareLogoImages.value.length > 0) return true
  return squareLogoInitialImages.value.some((image) => !squareLogoDeletedImages.value.includes(image.id))
})

// Pas de logo standard et pas de logo carré configuré (ou supprimé via la corbeille) : rien à proposer.
const showSquareLogoField = computed(() => {
  if (isLogoSquare.value) return false
  return Boolean(currentLogoSource.value) || hasSquareLogo.value
})

const hasLoyaltyProgram = computed(() => {
  return merchantColors.value.background !== null
})

// Le logo email est legacy : on ne propose plus d'en ajouter un, seuls les marchands qui en ont déjà un peuvent le gérer.
// La corbeille ne masque pas le champ : sinon impossible de remplacer le logo après l'avoir retiré.
const hasEmailLogo = computed(() => {
  return emailLogoImages.value.length > 0 || emailLogoInitialImages.value.length > 0
})

const getImageColors = async (image: string) => {
  const predominantColors = await getPredominantColors(image, true, defaultColor)

  form.value.background = predominantColors.primary
  form.value.button = predominantColors.secondary
}

const logoUpdate = async (newImages: ImageFile[]) => {
  logoImages.value = newImages
  if (newImages.length === 0) {
    form.value.logo = null
    return
  }
  form.value.logo = newImages[0].path
  await getImageColors(newImages[0].path)
}

const squareLogoUpdate = (newImages: ImageFile[]) => {
  squareLogoImages.value = newImages
  form.value.squareLogo = newImages.length === 0 ? null : newImages[0].path
}

const emailLogoUpdate = (newImages: ImageFile[]) => {
  emailLogoImages.value = newImages
  if (newImages.length === 0) {
    form.value.emailLogo = null
    return
  }
  form.value.emailLogo = newImages[0].path
}

const colorsHaveChanged = computed(() => {
  return merchantColors.value.background !== form.value.background || merchantColors.value.button !== form.value.button
})

// Bug de l'affichage du logo qui a du cache
const updateColors = async () => {
  return axios
    .put(`merchants/${user.value.merchant.id}/colors`, {
      background: form.value.background.substring(1),
      button: form.value.button.substring(1),
      text: getContrast(form.value.background).substring(1)
    })
    .catch((error) => {
      notification.create('error', {
        text: error.response.data.message
      })
    })
}
const updateMedia = async () => {
  return axios
    .put(`merchants/${user.value.merchant.id}/media`, {
      logo: form.value.logo,
      logoSquare: form.value.squareLogo,
      logoMail: form.value.emailLogo
    })
    .catch(() => {
      notification.create('error', {
        text: t('pages.settings.visualIdentity.form.mediaError')
      })
    })
}
const cleanForm = () => {
  if (form.value.logo) {
    merchantLogo.value = form.value.logo
  }
  if (form.value.squareLogo) {
    merchantSquareLogo.value = form.value.squareLogo
  }
  if (form.value.emailLogo) {
    merchantEmailLogo.value = form.value.emailLogo
  }
  logoImages.value = []
  squareLogoImages.value = []
  emailLogoImages.value = []
  logoDeletedImages.value = []
  squareLogoDeletedImages.value = []
  emailLogoDeletedImages.value = []
  syncInitialImages()
}

const checkPermission = () => {
  if (!hasPermissionBySlug('global-configuration.update')) {
    notification.create('error', {
      text: t('notifications.forbidden')
    })

    return false
  }

  return true
}
const isOnBoarding = () => {
  return merchantColors.value.background === defaultColor && merchantColors.value.button === defaultColor
}
const showModal = ref(false)
const handleSubmit = async () => {
  if (!checkPermission()) {
    return
  }

  if (colorsHaveChanged.value && !isOnBoarding()) {
    showModal.value = true
  } else {
    await saveVisualIdentity()
  }
}

const saveVisualIdentity = async () => {
  try {
    saveLoading.value = true
    await Promise.all([updateColors(), updateMedia()])

    notification.create('success', {
      text: t('pages.settings.visualIdentity.form.success')
    })
  } finally {
    saveLoading.value = false
    showModal.value = false
    cleanForm()

    const redirectUrl = route.query.redirect_url
    if (redirectUrl) {
      router.push({
        path: formattedRedirectUrl(redirectUrl)
      })
    }
  }
}
</script>

<template>
  <TheListHeader :title="merchantName">
    <template #actions>
      <Button
        variant="default-primary"
        type="submit"
        :disabled="saveLoading"
        class="fixed bottom-0 left-0 z-30 w-screen rounded-none py-4 sm:static sm:w-auto sm:rounded-control sm:py-2"
        @click="handleSubmit"
      >
        <Loader2 v-if="saveLoading" class="h-4 w-4 animate-spin" />
        {{ saveLoading ? t('global.words.saving') : t('pages.settings.visualIdentity.buttons.save') }}
      </Button>
    </template>
  </TheListHeader>
  <div class="px-6 pb-6 sm:px-12 sm:pb-12">
    <AppSkeleton v-if="fetchLoading" type="default" />
    <template v-else>
      <div class="mb-5 rounded-md border bg-white p-6">
        <div class="mb-6">
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ t('pages.settings.visualIdentity.panel.title') }}
          </h3>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ t('pages.settings.visualIdentity.panel.description') }}
          </p>
        </div>
        <div class="z-10 space-y-6">
          <div>
            <label class="mb-2 block text-sm font-medium text-foreground">
              {{ t('pages.settings.visualIdentity.logo.title') }}
            </label>
            <p class="mb-2 text-xs text-muted-foreground">
              {{ t('pages.settings.visualIdentity.logo.tip') }}
            </p>
            <ImageUploaderNew
              :images="logoImages"
              :initial-images="logoInitialImages"
              :deleted-images="logoDeletedImages"
              single-image
              crop
              fit-thumbnail
              @update:images="logoUpdate"
              @update:deleted-images="logoDeletedImages = $event"
            />
          </div>
          <div v-if="showSquareLogoField">
            <label class="mb-2 block text-sm font-medium text-foreground">
              {{ t('pages.settings.visualIdentity.squareLogo.title') }}
            </label>
            <p class="mb-2 text-xs text-muted-foreground">
              {{ t('pages.settings.visualIdentity.squareLogo.tip') }}
            </p>
            <ImageUploaderNew
              :images="squareLogoImages"
              :initial-images="squareLogoInitialImages"
              :deleted-images="squareLogoDeletedImages"
              :aspect-ratio="1"
              single-image
              crop
              @update:images="squareLogoUpdate"
              @update:deleted-images="squareLogoDeletedImages = $event"
            />
          </div>
          <template v-if="hasLoyaltyProgram">
            <div v-if="hasEmailLogo">
              <label class="mb-2 block text-sm font-medium text-foreground">
                {{ t('pages.settings.visualIdentity.emailLogo.title') }}
              </label>
              <p class="mb-2 text-xs text-muted-foreground">
                {{ t('pages.settings.visualIdentity.emailLogo.tip') }}
              </p>
              <ImageUploaderNew
                :images="emailLogoImages"
                :initial-images="emailLogoInitialImages"
                :deleted-images="emailLogoDeletedImages"
                single-image
                crop
                fit-thumbnail
                @update:images="emailLogoUpdate"
                @update:deleted-images="emailLogoDeletedImages = $event"
              />
            </div>
            <div class="flex flex-col gap-x-16 gap-y-8 lg:flex-row lg:gap-y-0 sm:border-t sm:border-border pt-6">
              <FormColorPicker
                id="backgroundsColor"
                v-model="form.background"
                class="grow"
                :label="t('pages.settings.visualIdentity.primaryColor.title')"
                :tip="t('pages.settings.visualIdentity.primaryColor.tip')"
              />
              <FormColorPicker
                id="buttonsColor"
                v-model="form.button"
                class="grow"
                :label="t('pages.settings.visualIdentity.secondaryColor.title')"
                :tip="t('pages.settings.visualIdentity.secondaryColor.tip')"
              />
            </div>
          </template>
        </div>
      </div>
    </template>

    <Dialog v-model:open="showModal" @update:open="showModal = false">
      <DialogContent class="sm:max-w-lg" :show-close-button="true">
        <div>
          <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <BaseIcon name="HeroExclamationTriangleIconOutline" class="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div class="mt-3 text-center sm:mt-5">
            <h2 class="text-lg font-semibold text-center mb-4">
              {{ t('pages.settings.visualIdentity.approvedChangesModal.title') }}
            </h2>
            <p class="text-sm text-muted-foreground text-center">
              <i18n-t keypath="pages.settings.visualIdentity.approvedChangesModal.description">
                <template #strong1>
                  <strong>{{ t('pages.settings.visualIdentity.approvedChangesModal.descriptionStrong1') }}</strong>
                </template>
                <template #strong2>
                  <strong>{{ t('pages.settings.visualIdentity.approvedChangesModal.descriptionStrong2') }}</strong>
                </template>
                <template #strong3>
                  <strong>{{ t('pages.settings.visualIdentity.approvedChangesModal.descriptionStrong3') }}</strong>
                </template>
                <template #breack>
                  <br /><br />
                  {{ t('pages.settings.visualIdentity.approvedChangesModal.descriptionBreack') }}
                </template>
              </i18n-t>
            </p>
          </div>
        </div>
        <div class="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
          <Button variant="ghost-primary" class="cursor-pointer" @click="showModal = false">
            {{ t('pages.settings.visualIdentity.approvedChangesModal.cancel') }}
          </Button>
          <Button variant="default-primary" :disabled="fetchLoading" class="cursor-pointer" @click="saveVisualIdentity">
            <Loader2 v-if="fetchLoading" class="h-4 w-4 animate-spin" />
            {{ t('pages.settings.visualIdentity.approvedChangesModal.confirm') }}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </div>
</template>
